import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Save,
  User,
  Phone,
  Briefcase,
  Users,
  FileText,
  Upload,
  Trash2,
  Eye,
  Image,
  File,
  Keyboard,
  ChevronDown,
  Check,
  AlertTriangle,
  Building2,
  GraduationCap,
  ClipboardCheck,
} from 'lucide-react';
import {
  getStaffProfileWithTenant,
  updateStaffProfileWithTenant,
  uploadStaffDocumentWithTenant,
  deleteStaffDocumentWithTenant,
  getStaffRolePermissionsWithTenant,
  updateStaffRoleWithTenant,
  fetchActiveCampusesWithTenant,
  fetchAcademicYearsWithTenant,
  fetchCountriesForExport,
  type StaffUser,
  type ProfessorUser,
} from '@/services/superAdminUserService';
import { COUNTRY_PHONE_CODES, DEFAULT_PHONE_COUNTRY } from '@/shared/constants/phoneCodes';
import { toCountryCode } from '@/shared/constants/countryCode';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';

interface DirectEditStaffProfileModalSuperAdminProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffUser | ProfessorUser;
  tenantId: string;
  onSuccess?: () => void;
}

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['\u0636', '\u0635', '\u062B', '\u0642', '\u0641', '\u063A', '\u0639', '\u0647', '\u062E', '\u062D', '\u062C'],
  ['\u0634', '\u0633', '\u064A', '\u0628', '\u0644', '\u0627', '\u062A', '\u0646', '\u0645', '\u0643', '\u0637'],
  ['\u0626', '\u0621', '\u0624', '\u0631', '\u0644\u0627', '\u0649', '\u0629', '\u0648', '\u0632', '\u0638', '\u062F'],
];

const STATUT_PROFESSEUR_OPTIONS = [
  { value: '', label: 'Selectionner...' },
  { value: 'P', label: 'Permanent' },
  { value: 'V', label: 'Vacataire' },
];

const genderOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Feminin' },
];

const handicapOptions = [
  { value: 'N', label: 'Aucun' },
  { value: 'A', label: 'Auditif' },
  { value: 'M', label: 'Moteur' },
  { value: 'V', label: 'Visuel' },
  { value: 'AM', label: 'Auditif et Moteur' },
  { value: 'AV', label: 'Auditif et Visuel' },
  { value: 'MV', label: 'Moteur et Visuel' },
  { value: 'T', label: 'Tous' },
  { value: 'XX', label: 'Autre' },
];

const staffDocumentTypes = [
  { type: 'CIN_FRONT', label: 'CIN (Recto)', description: 'Face avant de la carte d\'identite' },
  { type: 'CIN_BACK', label: 'CIN (Verso)', description: 'Face arriere de la carte d\'identite' },
  { type: 'PASSPORT', label: 'Passeport', description: 'Page d\'identite du passeport' },
  { type: 'PHOTO', label: 'Photo d\'identite', description: 'Photo d\'identite recente' },
  { type: 'CV', label: 'CV', description: 'Curriculum Vitae' },
  { type: 'CONTRAT_TRAVAIL', label: 'Contrat de travail', description: 'Contrat de travail signe' },
  { type: 'DIPLOME_SCAN', label: 'Diplome', description: 'Copie du diplome le plus eleve' },
  { type: 'ATTESTATION_RIB', label: 'Attestation RIB', description: 'Releve d\'identite bancaire' },
  { type: 'ENGAGEMENT_PROFESSEUR', label: 'Engagement des professeurs', description: 'Engagement signe - renouvelable chaque annee academique', professorOnly: true },
  { type: 'AUTORISATION_ENSEIGNER', label: 'Autorisation d\'enseigner', description: 'Vacataire issu d\'un etablissement public - renouvelable chaque annee', professorOnly: true, vacatairePublicOnly: true },
];

function parsePhoneNumber(phone: string | undefined | null): { countryCode: string; number: string } {
  if (!phone) return { countryCode: DEFAULT_PHONE_COUNTRY, number: '' };
  for (const country of COUNTRY_PHONE_CODES) {
    if (phone.startsWith(country.dialCode)) {
      return { countryCode: country.code, number: phone.slice(country.dialCode.length).trim() };
    }
  }
  const cleanNumber = phone.replace(/^\+/, '');
  return { countryCode: DEFAULT_PHONE_COUNTRY, number: cleanNumber };
}

// ==================== SUB-COMPONENTS ====================

const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;
        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">Arabic Keyboard</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button type="button" onClick={() => onKeyPress(' ')} className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200">
            espace
          </button>
          <button type="button" onClick={onBackspace} className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200">
            effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (containerRef.current && !containerRef.current.contains(e.target as Node) && (!keyboardElement || !keyboardElement.contains(e.target as Node))) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        onChange(value.slice(0, start - 1) + value.slice(end));
      } else if (start !== end) {
        onChange(value.slice(0, start) + value.slice(end));
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir="rtl"
        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) inputRef.current?.focus();
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${showKeyboard ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>
      {showKeyboard && <VirtualArabicKeyboard onKeyPress={handleKeyPress} onBackspace={handleBackspace} onClose={() => setShowKeyboard(false)} anchorRef={containerRef} />}
    </div>
  );
};

const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selected = COUNTRY_PHONE_CODES.find((c) => c.code === value) || COUNTRY_PHONE_CODES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px] h-[42px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRY_PHONE_CODES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => { onChange(country.code); setIsOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${value === country.code ? 'bg-primary-50' : ''}`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 text-sm">{country.name}</span>
              <span className="text-xs text-gray-500">{country.dialCode}</span>
              {value === country.code && <Check className="w-4 h-4 text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DeleteConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentName: string;
  isDeleting: boolean;
}> = ({ isOpen, onClose, onConfirm, documentName, isDeleting }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Supprimer le document</h3>
            <p className="text-sm text-gray-500 mt-1">Cette action est irreversible</p>
          </div>
        </div>
        <p className="text-gray-600 mb-6">
          Etes-vous sur de vouloir supprimer le document <strong className="text-gray-900">"{documentName}"</strong> ?
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={isDeleting} className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
            Annuler
          </button>
          <button onClick={onConfirm} disabled={isDeleting} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
            {isDeleting ? (<><Loader2 className="w-4 h-4 animate-spin" />Suppression...</>) : (<><Trash2 className="w-4 h-4" />Supprimer</>)}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ==================== MAIN COMPONENT ====================

export const DirectEditStaffProfileModalSuperAdmin: React.FC<DirectEditStaffProfileModalSuperAdminProps> = ({
  isOpen,
  onClose,
  staff,
  tenantId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<any>(null);
  const [phoneCountry, setPhoneCountry] = useState('MA');
  const [urgencePhoneCountry, setUrgencePhoneCountry] = useState('MA');
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [payrollCampusId, setPayrollCampusId] = useState<string>('');
  const [countries, setCountries] = useState<Array<{ code: string; nameFr: string }>>([]);
  const [campuses, setCampuses] = useState<Array<{ id: string; name: string; city?: string; isMainCampus?: boolean }>>([]);
  const [academicYears, setAcademicYears] = useState<Array<{ id: string; name: string; isCurrent?: boolean }>>([]);
  const [rolePermissions, setRolePermissions] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [originalRole, setOriginalRole] = useState<string>('');

  useEffect(() => {
    if (isOpen && staff && tenantId) {
      loadData();
    }
  }, [isOpen, staff?.id, tenantId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const [profileData, countriesData, campusesData, yearsData] = await Promise.all([
        getStaffProfileWithTenant(staff.id, tenantId),
        fetchCountriesForExport(),
        fetchActiveCampusesWithTenant(tenantId),
        fetchAcademicYearsWithTenant(tenantId),
      ]);

      setProfile(profileData);
      setCountries(countriesData || []);
      setCampuses(campusesData?.campuses || []);
      setAcademicYears(yearsData || []);

      if (profileData) {
        setDocuments(profileData.documents || []);
        setSelectedCampusIds(profileData.campuses?.map((c: any) => c.id) || []);
        setSelectedAcademicYearId(profileData.academicYear?.id || '');
        setPayrollCampusId(profileData.payrollCampusId || '');

        const userProfile = profileData.profile;
        const parsedPhone = parsePhoneNumber(profileData.phone);
        const parsedUrgencePhone = parsePhoneNumber(userProfile?.urgenceNumero);

        const phoneCountryValue = userProfile?.phoneCountry || parsedPhone.countryCode;
        const urgencePhoneCountryValue = userProfile?.urgencePhoneCountry || parsedUrgencePhone.countryCode;

        setPhoneCountry(phoneCountryValue);
        setUrgencePhoneCountry(urgencePhoneCountryValue);

        setFormData({
          nom: profileData.lastName || '',
          prenom: profileData.firstName || '',
          nomArabe: userProfile?.nomArabe || '',
          prenomArabe: userProfile?.prenomArabe || '',
          cnie: userProfile?.cnie || '',
          passport: userProfile?.passport || '',
          sexe: userProfile?.sexe || '',
          dateNaissance: userProfile?.dateNaissance || '',
          paysOrigine: toCountryCode(userProfile?.paysOrigine),
          handicap: userProfile?.handicap || '',
          email: profileData.email || '',
          telephone: parsedPhone.number,
          phoneCountry: phoneCountryValue,
          adresse: userProfile?.adresse || '',
          ville: userProfile?.ville || '',
          codePostal: userProfile?.codePostal || '',
          urgenceNom: userProfile?.urgenceNom || '',
          urgenceRelation: userProfile?.urgenceRelation || '',
          urgencePhoneCountry: urgencePhoneCountryValue,
          urgenceNumero: parsedUrgencePhone.number,
          urgenceAdresse: userProfile?.urgenceAdresse || '',
          dateRecrutement: userProfile?.dateRecrutement || '',
          fonction: userProfile?.fonction || '',
          diplomeId: userProfile?.diplomeId || '',
          specialite: userProfile?.specialite || '',
          matieresEnseignees: userProfile?.matieresEnseignees || '',
          statutProfesseur: userProfile?.statutProfesseur || '',
          tauxHoraire: userProfile?.tauxHoraire || '',
          gradeId: userProfile?.gradeId || '',
          estOriginaireEtablissementPublic: userProfile?.estOriginaireEtablissementPublic || '',
          etablissementOrigine: userProfile?.etablissementOrigine || '',
          departementAttacheId: userProfile?.departementAttacheId || '',
          rib: userProfile?.rib || '',
          salaireMensuelNet: userProfile?.salaireMensuelNet || '',
          isAlsoProfessor: profileData.isAlsoProfessor || false,
          physicalDocCin: userProfile?.physicalDocCin || false,
          physicalDocPassport: userProfile?.physicalDocPassport || false,
          physicalDocCv: userProfile?.physicalDocCv || false,
          physicalDocContratTravail: userProfile?.physicalDocContratTravail || false,
          physicalDocDiplome: userProfile?.physicalDocDiplome || false,
          physicalDocAttestationRib: userProfile?.physicalDocAttestationRib || false,
          physicalDocEngagementProfesseur: userProfile?.physicalDocEngagementProfesseur || false,
          physicalDocReglementInterieur: userProfile?.physicalDocReglementInterieur || false,
          physicalDocConsentement: userProfile?.physicalDocConsentement || false,
          physicalDocAutorisationEnseigner: userProfile?.physicalDocAutorisationEnseigner || false,
        });

        setSelectedRole(profileData.role);
        setOriginalRole(profileData.role);
      }

      try {
        const rolePerms = await getStaffRolePermissionsWithTenant(staff.id, tenantId);
        setRolePermissions(rolePerms);
      } catch {
        setRolePermissions(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
  };

  const handleCampusToggle = (campusId: string) => {
    setSelectedCampusIds(prev =>
      prev.includes(campusId) ? prev.filter(id => id !== campusId) : [...prev, campusId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (selectedRole && selectedRole !== originalRole && rolePermissions?.canEdit) {
        await updateStaffRoleWithTenant(staff.id, tenantId, selectedRole);
        setOriginalRole(selectedRole);
      }

      // matieresEnseignees is system-generated from course assignments — never sent
      const { matieresEnseignees: _matieresEnseignees, ...editableData } = formData;
      const dataToSave = {
        ...editableData,
        campusIds: selectedCampusIds,
        academicYearId: selectedAcademicYearId || undefined,
        payrollCampusId: payrollCampusId || undefined,
      };

      await updateStaffProfileWithTenant(staff.id, tenantId, dataToSave);
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setProfile(null);
    setDocuments([]);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const getDocumentByType = (type: string) => documents.find(d => d.documentType === type);

  const handleUploadClick = (docType: string) => {
    setSelectedDocType(docType);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocType) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Type de fichier non supporte. Utilisez JPG, PNG, WebP ou PDF.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. Taille maximum: 10 Mo.');
      return;
    }

    setUploadingDoc(selectedDocType);
    setError(null);

    try {
      const uploadedDoc = await uploadStaffDocumentWithTenant(staff.id, tenantId, selectedDocType, file);
      setDocuments(prev => {
        const filtered = prev.filter(d => d.documentType !== selectedDocType);
        return [...filtered, uploadedDoc];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du telechargement');
    } finally {
      setUploadingDoc(null);
      setSelectedDocType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocumentClick = (doc: any) => {
    setDocToDelete(doc);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    setDeletingDocId(docToDelete.id);
    setError(null);

    try {
      await deleteStaffDocumentWithTenant(staff.id, tenantId, docToDelete.id);
      setDocuments(prev => prev.filter(d => d.id !== docToDelete.id));
      setDeleteModalOpen(false);
      setDocToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setDeletingDocId(null);
    }
  };

  const getFileIcon = (mimeType?: string) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-red-500" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const showProfessorFields = profile?.role === 'TEACHER' || formData.isAlsoProfessor;
  // Autorisation d'enseigner only concerns vacataires from a public institution
  const isVacataireFromPublic =
    showProfessorFields &&
    (formData.statutProfesseur || '').toUpperCase() === 'V' &&
    (formData.estOriginaireEtablissementPublic || '').toUpperCase() === 'O';
  const visibleDocumentTypes = staffDocumentTypes.filter((d) => {
    if (d.vacatairePublicOnly) return isVacataireFromPublic;
    if (d.professorOnly) return showProfessorFields;
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={handleFileSelect}
        />

        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-primary-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Modifier le profil</h2>
                <p className="text-sm text-gray-500">{staff.firstName} {staff.lastName}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
                <p className="text-gray-500">Chargement du profil...</p>
              </div>
            ) : error && !profile ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <button onClick={loadData} className="text-sm text-red-600 hover:text-red-800 underline mt-2">Reessayer</button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-green-700">Profil mis a jour avec succes!</p>
                  </div>
                )}

                {/* Section 1: Identite */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 1: Identite</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input type="text" value={formData.nom || ''} onChange={(e) => handleInputChange('nom', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prenom</label>
                      <input type="text" value={formData.prenom || ''} onChange={(e) => handleInputChange('prenom', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom (Arabe)</label>
                      <ArabicInput value={formData.nomArabe || ''} onChange={(value) => handleInputChange('nomArabe', value)} placeholder="Arabic name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prenom (Arabe)</label>
                      <ArabicInput value={formData.prenomArabe || ''} onChange={(value) => handleInputChange('prenomArabe', value)} placeholder="Arabic first name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CNIE</label>
                      <input type="text" value={formData.cnie || ''} onChange={(e) => handleInputChange('cnie', e.target.value)} placeholder="Ex: AB123456" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Passeport</label>
                      <input type="text" value={formData.passport || ''} onChange={(e) => handleInputChange('passport', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                      <select value={formData.sexe || ''} onChange={(e) => handleInputChange('sexe', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Selectionner --</option>
                        {genderOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                      <input type="date" value={formData.dateNaissance || ''} onChange={(e) => handleInputChange('dateNaissance', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'origine</label>
                      <select value={formData.paysOrigine || ''} onChange={(e) => handleInputChange('paysOrigine', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Selectionner --</option>
                        {countries.map(c => <option key={c.code} value={c.code}>{c.nameFr}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Situation de handicap</label>
                      <select value={formData.handicap || ''} onChange={(e) => handleInputChange('handicap', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Selectionner --</option>
                        {handicapOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Coordonnees */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 2: Coordonnees</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={formData.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
                      <div className="flex">
                        <PhoneCountrySelector value={phoneCountry} onChange={(code) => { setPhoneCountry(code); handleInputChange('phoneCountry', code); }} />
                        <input type="tel" value={formData.telephone || ''} onChange={(e) => handleInputChange('telephone', e.target.value)} placeholder="6XX XXX XXX" className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                      </div>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input type="text" value={formData.adresse || ''} onChange={(e) => handleInputChange('adresse', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                  </div>
                </div>

                {/* Section 3: Contact d'Urgence */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 3: Contact d'Urgence</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact</label>
                      <input type="text" value={formData.urgenceNom || ''} onChange={(e) => handleInputChange('urgenceNom', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                      <select value={formData.urgenceRelation || ''} onChange={(e) => handleInputChange('urgenceRelation', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        {emergencyRelationSelectOptions(formData.urgenceRelation).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
                      <div className="flex">
                        <PhoneCountrySelector value={urgencePhoneCountry} onChange={(code) => { setUrgencePhoneCountry(code); handleInputChange('urgencePhoneCountry', code); }} />
                        <input type="tel" value={formData.urgenceNumero || ''} onChange={(e) => handleInputChange('urgenceNumero', e.target.value)} placeholder="6XX XXX XXX" className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                      </div>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input type="text" value={formData.urgenceAdresse || ''} onChange={(e) => handleInputChange('urgenceAdresse', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                  </div>
                </div>

                {/* Section 4: Informations Professionnelles */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 4: Informations Professionnelles</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rolePermissions?.canEdit && rolePermissions.assignableRoles?.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type de compte</label>
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value={originalRole}>
                            {rolePermissions.currentRoleInfo?.nameFr || originalRole}
                          </option>
                          {rolePermissions.assignableRoles
                            .filter((r: any) => r.role !== originalRole)
                            .sort((a: any, b: any) => a.level - b.level)
                            .map((role: any) => (
                              <option key={role.role} value={role.role}>
                                {role.nameFr}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de recrutement</label>
                      <input type="date" value={formData.dateRecrutement || ''} onChange={(e) => handleInputChange('dateRecrutement', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                      <input type="text" value={formData.fonction || ''} onChange={(e) => handleInputChange('fonction', e.target.value)} placeholder="Ex: Professeur Assistant" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Diplome</label>
                      <select value={formData.diplomeId || ''} onChange={(e) => handleInputChange('diplomeId', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Selectionner --</option>
                        {profile?.nomenclature?.diplomes?.map((d: any) => <option key={d.id} value={d.id}>{d.labelFr}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specialite</label>
                      <input type="text" value={formData.specialite || ''} onChange={(e) => handleInputChange('specialite', e.target.value)} placeholder="Ex: Informatique" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">RIB bancaire</label>
                      <input type="text" value={formData.rib || ''} onChange={(e) => handleInputChange('rib', e.target.value)} placeholder="24 chiffres" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Salaire mensuel net</label>
                      <input type="text" value={formData.salaireMensuelNet || ''} onChange={(e) => handleInputChange('salaireMensuelNet', e.target.value)} placeholder="Ex: 15000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                  </div>
                </div>

                {/* Section: Activite d'enseignement (for non-TEACHER roles) */}
                {profile?.role !== 'TEACHER' && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-amber-50 border-b border-gray-200 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-amber-600" />
                      <h3 className="font-medium text-gray-900">Activite d'enseignement</h3>
                    </div>
                    <div className="p-4">
                      <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.isAlsoProfessor || false}
                          onChange={(e) => handleInputChange('isAlsoProfessor', e.target.checked)}
                          className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Ce membre du personnel enseigne egalement</span>
                          <p className="text-xs text-gray-500">Cochez si ce membre du personnel a des activites d'enseignement</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Section 5: Informations Professeur (conditionally shown) */}
                {showProfessorFields && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-purple-50 border-b border-gray-200 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-purple-600" />
                      <h3 className="font-medium text-gray-900">Section 5: Informations Professeur / E-DEP</h3>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut professeur</label>
                        <select value={formData.statutProfesseur || ''} onChange={(e) => handleInputChange('statutProfesseur', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          {STATUT_PROFESSEUR_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                        <select value={formData.gradeId || ''} onChange={(e) => handleInputChange('gradeId', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          <option value="">-- Selectionner --</option>
                          {profile?.nomenclature?.grades?.map((g: any) => <option key={g.id} value={g.id}>{g.labelFr}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Departement d'attache</label>
                        <select value={formData.departementAttacheId || ''} onChange={(e) => handleInputChange('departementAttacheId', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          <option value="">-- Selectionner --</option>
                          {profile?.nomenclature?.departments?.map((d: any) => <option key={d.id} value={d.id}>{d.labelFr}</option>)}
                        </select>
                      </div>
                      {formData.statutProfesseur === 'V' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Taux horaire (MAD)</label>
                            <input type="text" value={formData.tauxHoraire || ''} onChange={(e) => handleInputChange('tauxHoraire', e.target.value)} placeholder="Ex: 200" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Etablissement d'origine</label>
                            <input type="text" value={formData.etablissementOrigine || ''} onChange={(e) => handleInputChange('etablissementOrigine', e.target.value)} placeholder="Ex: Universite Mohammed V" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Originaire d'etablissement public</label>
                            <select value={formData.estOriginaireEtablissementPublic || ''} onChange={(e) => handleInputChange('estOriginaireEtablissementPublic', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                              <option value="">-- Selectionner --</option>
                              <option value="O">Oui</option>
                              <option value="N">Non</option>
                            </select>
                          </div>
                        </>
                      )}
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Matieres enseignees</label>
                        <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 min-h-[42px]">{formData.matieresEnseignees || <span className="text-gray-400 italic">Sera genere automatiquement apres l'affectation des cours</span>}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 6: Affectation */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 6: Affectation</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Campus (selection multiple)</label>
                      <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                        {campuses.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun campus disponible</p>
                        ) : (
                          campuses.map(campus => (
                            <label key={campus.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedCampusIds.includes(campus.id)}
                                onChange={() => handleCampusToggle(campus.id)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">{campus.name} {campus.city && `(${campus.city})`}</span>
                              {campus.isMainCampus && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Principal</span>}
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annee academique</label>
                      <select value={selectedAcademicYearId} onChange={(e) => setSelectedAcademicYearId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Non assignee --</option>
                        {academicYears.map(ay => (
                          <option key={ay.id} value={ay.id}>
                            {ay.name} {ay.isCurrent && '(En cours)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    {campuses.length > 1 && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Campus de Paie {selectedCampusIds.length > 1 && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={payrollCampusId}
                          onChange={(e) => setPayrollCampusId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">-- Selectionner un campus --</option>
                          {campuses.map(campus => (
                            <option key={campus.id} value={campus.id}>
                              {campus.name} {campus.city && `(${campus.city})`}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Le campus responsable du paiement de ce membre du personnel
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 7: Documents */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 7: Documents</h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {visibleDocumentTypes.map(docType => {
                        const existingDoc = getDocumentByType(docType.type);
                        const isUploading = uploadingDoc === docType.type;
                        const isDeleting = deletingDocId === existingDoc?.id;

                        return (
                          <div key={docType.type} className={`border rounded-lg p-4 ${existingDoc ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm">{docType.label}</h4>
                                <p className="text-xs text-gray-500 mt-0.5">{docType.description}</p>
                              </div>
                            </div>

                            {existingDoc ? (
                              <div className="mt-3">
                                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                  {getFileIcon(existingDoc.mimeType)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-700 truncate">{existingDoc.fileName}</p>
                                    <p className="text-xs text-gray-500">{formatFileSize(existingDoc.fileSize)}</p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <a href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${existingDoc.fileUrl}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors" title="Voir">
                                      <Eye className="w-4 h-4" />
                                    </a>
                                    <button onClick={() => handleDeleteDocumentClick(existingDoc)} disabled={isDeleting} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50" title="Supprimer">
                                      {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    </button>
                                  </div>
                                </div>
                                <button onClick={() => handleUploadClick(docType.type)} disabled={isUploading} className="mt-2 w-full px-3 py-1.5 text-xs text-primary-600 border border-primary-300 rounded hover:bg-primary-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1">
                                  {isUploading ? (<><Loader2 className="w-3 h-3 animate-spin" />Telechargement...</>) : (<><Upload className="w-3 h-3" />Remplacer</>)}
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => handleUploadClick(docType.type)} disabled={isUploading} className="mt-3 w-full px-3 py-2 text-sm text-gray-600 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {isUploading ? (<><Loader2 className="w-4 h-4 animate-spin" />Telechargement...</>) : (<><Upload className="w-4 h-4" />Telecharger</>)}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-4">Formats acceptes: JPG, PNG, WebP, PDF. Taille maximum: 10 Mo par fichier.</p>
                  </div>
                </div>

                {/* Section 8: Suivi des copies physiques */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 8: Suivi des copies physiques</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Cochez les documents physiques recus pour ce membre du personnel.
                    </p>
                    <div className="space-y-3">
                      {formData.paysOrigine === 'MA' || formData.paysOrigine === '' ? (
                        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.physicalDocCin || false}
                            onChange={(e) => handleInputChange('physicalDocCin', e.target.checked)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <div>
                            <span className="font-medium text-gray-900">CIN</span>
                            <p className="text-xs text-gray-500">Carte d'identite nationale</p>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.physicalDocPassport || false}
                            onChange={(e) => handleInputChange('physicalDocPassport', e.target.checked)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <div>
                            <span className="font-medium text-gray-900">Passeport</span>
                            <p className="text-xs text-gray-500">Copie du passeport</p>
                          </div>
                        </label>
                      )}

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocCv || false}
                          onChange={(e) => handleInputChange('physicalDocCv', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">CV</span>
                          <p className="text-xs text-gray-500">Curriculum Vitae</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocContratTravail || false}
                          onChange={(e) => handleInputChange('physicalDocContratTravail', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Contrat de travail</span>
                          <p className="text-xs text-gray-500">Contrat de travail signe</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocDiplome || false}
                          onChange={(e) => handleInputChange('physicalDocDiplome', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Diplome</span>
                          <p className="text-xs text-gray-500">Copie du diplome le plus eleve</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocAttestationRib || false}
                          onChange={(e) => handleInputChange('physicalDocAttestationRib', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Attestation RIB</span>
                          <p className="text-xs text-gray-500">Releve d'identite bancaire</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocReglementInterieur || false}
                          onChange={(e) => handleInputChange('physicalDocReglementInterieur', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Reglement interieur</span>
                          <p className="text-xs text-gray-500">Reglement interieur signe</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.physicalDocConsentement || false}
                          onChange={(e) => handleInputChange('physicalDocConsentement', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Consentement medias</span>
                          <p className="text-xs text-gray-500">Consentement medias et reseaux sociaux signe</p>
                        </div>
                      </label>

                      {showProfessorFields && (
                        <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.physicalDocEngagementProfesseur || false}
                            onChange={(e) => handleInputChange('physicalDocEngagementProfesseur', e.target.checked)}
                            className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                          />
                          <div>
                            <span className="font-medium text-amber-900">Engagement des professeurs</span>
                            <p className="text-xs text-amber-700">Document d'engagement pour les activites d'enseignement (annee en cours)</p>
                          </div>
                        </label>
                      )}

                      {isVacataireFromPublic && (
                        <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.physicalDocAutorisationEnseigner || false}
                            onChange={(e) => handleInputChange('physicalDocAutorisationEnseigner', e.target.checked)}
                            className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                          />
                          <div>
                            <span className="font-medium text-amber-900">Autorisation d'enseigner</span>
                            <p className="text-xs text-amber-700">Vacataire issu d'un etablissement public - copie physique de l'autorisation</p>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button onClick={handleClose} disabled={saving} className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors">
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving || loading || success} className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium">
              {saving ? (<><Loader2 className="w-4 h-4 animate-spin" />Enregistrement...</>) : success ? (<><CheckCircle className="w-4 h-4" />Enregistre!</>) : (<><Save className="w-4 h-4" />Enregistrer les modifications</>)}
            </button>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal isOpen={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDocToDelete(null); }} onConfirm={handleConfirmDelete} documentName={docToDelete?.fileName || ''} isDeleting={!!deletingDocId} />
    </div>
  );
};

export default DirectEditStaffProfileModalSuperAdmin;
